import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import GettingStartedService from "../../../lib/gettingStartedService";
import GettingStarted from "./GettingStarted";
import OnboardingComplete from "./OnboardingComplete";

const OnboardingSection = () => {
  const { user } = useAuth();
  const [isCompleted, setIsCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isCheckingRef = useRef(false);

  // Silent check completion status without loading states
  const silentCheckCompletionStatus = async () => {
    if (!user?.id || isCheckingRef.current) return;

    isCheckingRef.current = true;

    try {
      const result = await GettingStartedService.getUserProgress(user.id);

      if (result.success && result.data) {
        const { totalSteps, completedSteps } = result.data;
        const newCompletionStatus =
          totalSteps > 0 && completedSteps === totalSteps;

        // Only update if status actually changed
        if (newCompletionStatus !== isCompleted) {
          setIsCompleted(newCompletionStatus);
        }
      }
    } catch (err) {
      console.error("Silent completion check error:", err);
      // Don't update error state during silent checks
    } finally {
      isCheckingRef.current = false;
    }
  };

  // Initial check with loading state
  const initialCheckCompletionStatus = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await GettingStartedService.getUserProgress(user.id);

      if (result.success && result.data) {
        const { totalSteps, completedSteps } = result.data;
        setIsCompleted(totalSteps > 0 && completedSteps === totalSteps);
      } else {
        setIsCompleted(false);
        if (result.error) {
          setError(result.error);
        }
      }
    } catch (err) {
      console.error("Error checking completion status:", err);
      setError("Failed to check onboarding status");
      setIsCompleted(false);
    } finally {
      setLoading(false);
    }
  };

  // Initial check on mount and when user changes
  useEffect(() => {
    initialCheckCompletionStatus();
  }, [user?.id]);

  // Listen for completion events to update status silently
  useEffect(() => {
    const handleStepCompletion = (event) => {
      // For individual steps, do silent check
      silentCheckCompletionStatus();
    };

    const handleProgressUpdated = (event) => {
      // For full completion, update immediately without API call
      if (event.detail?.completed === true) {
        setIsCompleted(true);
      } else {
        // Do silent check for other progress updates
        silentCheckCompletionStatus();
      }
    };

    // Listen for custom events that indicate step completion
    window.addEventListener(
      "gettingStarted:stepCompleted",
      handleStepCompletion,
    );
    window.addEventListener(
      "gettingStarted:progressUpdated",
      handleProgressUpdated,
    );

    return () => {
      window.removeEventListener(
        "gettingStarted:stepCompleted",
        handleStepCompletion,
      );
      window.removeEventListener(
        "gettingStarted:progressUpdated",
        handleProgressUpdated,
      );
    };
  }, [isCompleted, user?.id]);

  // Show loading state
  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
            <span className="text-muted-foreground">
              Loading onboarding status...
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Show error state with fallback to getting started
  if (error) {
    console.warn("Onboarding section error:", error);
    // Fallback to showing getting started on error
    return <GettingStarted />;
  }

  // Conditional rendering based on completion status
  return isCompleted ? <OnboardingComplete /> : <GettingStarted />;
};

export default OnboardingSection;
